// Global Instructions Rule Applied!
//
// HTTP layer for the Configuration → Pipelines tab (full pipeline & stage CRUD).
// Talks to the Python backend only; every read row is Zod-validated before it
// reaches the screen (invalid rows are dropped with a dev warning, never coerced).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  PipelinesAdminGridModel,
  validatePipelinesAdminGrid
} from '../../../state_manager/data_models/pipelines_admin_grid_model.ts'
import {
  PipelineStageAdminModel,
  validatePipelineStageAdmin
} from '../../../state_manager/data_models/pipeline_stage_admin_model.ts'
import { StageLibraryModel, validateStageLibrary } from '../../../state_manager/data_models/stage_library_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface ConfigSort {
  key: string
  direction: 'asc' | 'desc'
}

export interface WriteResponse {
  success: boolean
  data?: unknown
  error?: string
}

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = await readJsonBody<{ message?: string }>(response)
  return body?.message || `${fallback}: ${response.status} ${response.statusText}`
}

function validateRows<T>(rawRows: unknown[], validate: (row: unknown) => T, label: string): T[] {
  const out: T[] = []
  for (const row of rawRows) {
    try {
      out.push(validate(row))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn(`${label} row failed validation:`, e, row)
    }
  }
  return out
}

async function write(url: string, method: string, accessToken: string | null, body?: unknown): Promise<WriteResponse> {
  try {
    const response = await fetch(url, {
      method,
      headers: authHeaders(accessToken),
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Request failed') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// ---- Pipelines (master) ----

export const fetchPipelines = async (
  sorting: ConfigSort[] = [],
  filters: { key: string; value: unknown }[] = [],
  page = 1,
  pageSize = 200,
  accessToken: string | null = null
): Promise<{ data: PipelinesAdminGridModel[]; hasMore: boolean; totalCount: number; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINES_FILTER), {
      method: API_ROUTES.CONFIG.PIPELINES_FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { page, page_size: pageSize, sorting, filters } })
    })
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to fetch pipelines'))
    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = validateRows(Array.isArray(json.data) ? json.data : [], validatePipelinesAdminGrid, 'Pipeline')
    return { data: rows, hasMore: Boolean(json.has_more), totalCount: typeof json.count === 'number' ? json.count : rows.length }
  } catch (error) {
    return { data: [], hasMore: false, totalCount: 0, error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

export const createPipeline = (fields: Record<string, unknown>, token: string | null): Promise<WriteResponse> =>
  write(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINES_CREATE), API_ROUTES.CONFIG.PIPELINES_CREATE.method, token, { data: fields })

export const updatePipeline = (pipelineId: string, fields: Record<string, unknown>, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINES_UPDATE, { pipelineId }),
    API_ROUTES.CONFIG.PIPELINES_UPDATE.method,
    token,
    { data: fields }
  )

export const deletePipeline = (pipelineId: string, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINES_DELETE, { pipelineId }),
    API_ROUTES.CONFIG.PIPELINES_DELETE.method,
    token
  )

// ---- Pipeline stages (detail) + stage library ----

export const fetchPipelineStages = async (
  pipelineId: string,
  accessToken: string | null
): Promise<{ data: PipelineStageAdminModel[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINE_STAGES, { pipelineId }),
      { method: API_ROUTES.CONFIG.PIPELINE_STAGES.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) return { data: [], error: await errorMessage(response, 'Failed to fetch pipeline stages') }
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateRows(Array.isArray(json.data) ? json.data : [], validatePipelineStageAdmin, 'Pipeline stage') }
  } catch (error) {
    return { data: [], error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

export const fetchStageLibrary = async (
  accessToken: string | null
): Promise<{ data: StageLibraryModel[]; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_LIBRARY), {
      method: API_ROUTES.CONFIG.STAGE_LIBRARY.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: [], error: await errorMessage(response, 'Failed to fetch stage library') }
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateRows(Array.isArray(json.data) ? json.data : [], validateStageLibrary, 'Stage') }
  } catch (error) {
    return { data: [], error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

export const createStage = (fields: Record<string, unknown>, token: string | null): Promise<WriteResponse> =>
  write(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGES_CREATE), API_ROUTES.CONFIG.STAGES_CREATE.method, token, { data: fields })

export const updateStage = (stageId: string, fields: Record<string, unknown>, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGES_UPDATE, { stageId }),
    API_ROUTES.CONFIG.STAGES_UPDATE.method,
    token,
    { data: fields }
  )

export const deleteStage = (stageId: string, token: string | null): Promise<WriteResponse> =>
  write(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGES_DELETE, { stageId }), API_ROUTES.CONFIG.STAGES_DELETE.method, token)

// ---- Pipeline config (stage membership + per-stage settings) ----

export const createPipelineConfig = (fields: Record<string, unknown>, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINE_CONFIG_CREATE),
    API_ROUTES.CONFIG.PIPELINE_CONFIG_CREATE.method,
    token,
    { data: fields }
  )

export const updatePipelineConfig = (configId: string, fields: Record<string, unknown>, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINE_CONFIG_UPDATE, { configId }),
    API_ROUTES.CONFIG.PIPELINE_CONFIG_UPDATE.method,
    token,
    { data: fields }
  )

export const deletePipelineConfig = (configId: string, pipelineId: string, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINE_CONFIG_DELETE, { configId }, { pipeline_id: pipelineId }),
    API_ROUTES.CONFIG.PIPELINE_CONFIG_DELETE.method,
    token
  )
